import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Bell, CheckCheck } from "lucide-react";
import { api } from "@/lib/api";
import { formatDistanceToNow } from "date-fns";

export default function NotificationsBell() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const buttonRef = useRef(null);
  const panelRef = useRef(null);
  const [anchor, setAnchor] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems(data || []);
    } catch {
      setItems([]);
    }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, []);

  // Compute panel position relative to the bell button in viewport coords.
  // Rendered via portal to escape the header's stacking context, so the
  // panel is never clipped by mobile viewports or backdrop-blurred parents.
  const positionPanel = () => {
    const btn = buttonRef.current;
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    const margin = 8;
    const vw = document.documentElement.clientWidth || window.innerWidth;
    const width = Math.min(320, vw - margin * 2);
    // Prefer to anchor the panel's right edge to the button's right edge,
    // but clamp so the panel is always fully inside the viewport.
    let left = rect.right - width;
    if (left < margin) left = margin;
    if (left + width > vw - margin) left = vw - margin - width;
    const top = rect.bottom + margin;
    setAnchor({ top, left, width });
  };

  useLayoutEffect(() => {
    if (!open) return;
    positionPanel();
    const onResize = () => positionPanel();
    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", onResize, true);
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onResize, true);
    };
  }, [open]);

  // Close on outside click / Escape.
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      const b = buttonRef.current;
      const p = panelRef.current;
      if ((b && b.contains(e.target)) || (p && p.contains(e.target))) return;
      setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("touchstart", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("touchstart", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const unread = items.filter((i) => !i.read).length;

  const markAll = async () => {
    await api.post("/notifications/read-all");
    load();
  };

  return (
    <>
      <button
        ref={buttonRef}
        onClick={() => setOpen((v) => !v)}
        className="relative w-9 h-9 rounded-full bg-white border border-slate-200 hover:border-orange-300 flex items-center justify-center transition-colors"
        data-testid="notifications-bell"
        aria-label="Notifications"
        aria-expanded={open}
      >
        <Bell size={18} className="text-slate-700" />
        {unread > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center">
            {unread}
          </span>
        )}
      </button>

      {open && anchor && createPortal(
        <div
          ref={panelRef}
          style={{
            position: "fixed",
            top: anchor.top,
            left: anchor.left,
            width: anchor.width,
            maxHeight: `calc(100vh - ${anchor.top + 16}px)`,
          }}
          className="bg-white rounded-2xl border border-slate-100 shadow-2xl z-[100] overflow-hidden flex flex-col"
          data-testid="notifications-panel"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
            <div className="font-display font-semibold text-slate-900">Notifications</div>
            {unread > 0 && (
              <button
                onClick={markAll}
                className="text-xs font-semibold text-orange-600 hover:text-orange-700 flex items-center gap-1"
                data-testid="notifications-mark-all"
              >
                <CheckCheck size={14} /> Mark all read
              </button>
            )}
          </div>
          <div className="overflow-y-auto">
            {items.length === 0 ? (
              <div className="text-center text-sm text-slate-500 py-10 px-4">
                You're all caught up. New activity will show here.
              </div>
            ) : (
              items.map((n) => (
                <div
                  key={n.notification_id}
                  className={`px-4 py-3 border-b border-slate-50 ${!n.read ? "bg-amber-50/60" : ""}`}
                  data-testid={`notification-${n.notification_id}`}
                >
                  <div className="flex items-start gap-2">
                    {!n.read && <span className="mt-1.5 w-2 h-2 rounded-full bg-orange-500 flex-shrink-0" />}
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-slate-900">{n.title}</div>
                      <div className="text-xs text-slate-600 mt-0.5 leading-relaxed">{n.body}</div>
                      <div className="text-[11px] text-slate-400 mt-1">
                        {(() => {
                          try {
                            return formatDistanceToNow(new Date(n.created_at), { addSuffix: true });
                          } catch { return ""; }
                        })()}
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
